import React, { useEffect, useState } from 'react';
import { Check, Trash2 } from 'lucide-react';
import Modal from './Modal';

// MediaLinkDialog — shared across the Shoots page and the project page's
// DeliverableChecklist. Three flows from one component:
//
//   mode='complete' — open shoot being marked delivered. URL is optional.
//                     Caller wires onSubmit to POST /complete which also
//                     flips status=delivered + sets completed_date.
//
//   mode='edit'     — already-delivered shoot whose URL the user wants to
//                     change. Caller wires onSubmit to PATCH the deliverable
//                     so completed_date isn't reset on re-edit.
//                     "Remove link" button is shown when an existingUrl is
//                     present.
//
//   mode='add'      — already-delivered shoot with no URL yet (backfilling
//                     a project completed before the URL field existed).
//                     Same behavior as 'edit' but the dialog title and
//                     button copy reflect the empty starting state.
//
// onSubmit receives { deliverableId, mediaUrl, mode } where mediaUrl is the
// trimmed string or null when the user cleared/skipped it.
export default function MediaLinkDialog({
  open, onClose, onSubmit,
  mode = 'complete',
  deliverableId,
  headerTitle,
  subtitle,
  existingUrl = '',
}) {
  const [mediaUrl, setMediaUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittingMode, setSubmittingMode] = useState(null); // 'save' | 'remove'
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setMediaUrl(mode === 'complete' ? '' : existingUrl || '');
      setSubmitting(false);
      setSubmittingMode(null);
      setError('');
    }
  }, [open, mode, existingUrl]);

  if (!open) return null;

  const submitWith = async (urlValue) => {
    const trimmed = (urlValue || '').trim();
    if (trimmed) {
      try { new URL(trimmed); } catch {
        setError("That URL doesn't look valid. Include https:// (or remove the link entirely).");
        return;
      }
    }
    setSubmitting(true);
    setSubmittingMode(trimmed ? 'save' : 'remove');
    setError('');
    try {
      await onSubmit({ deliverableId, mediaUrl: trimmed || null, mode });
    } catch (err) {
      setError(err?.message || (mode === 'complete' ? 'Failed to mark complete' : 'Failed to save link'));
      setSubmitting(false);
      setSubmittingMode(null);
    }
  };

  const submit = (e) => { e?.preventDefault?.(); submitWith(mediaUrl); };
  const remove = () => submitWith('');

  const isEdit = mode === 'edit' || mode === 'add';
  const title = mode === 'complete'
    ? 'Mark shoot complete'
    : existingUrl ? 'Edit media link' : 'Add media link';
  const primaryLabel = mode === 'complete' ? 'Mark complete' : 'Save link';

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={title}
      maxWidth="max-w-md"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          {isEdit && existingUrl ? (
            <button
              type="button"
              onClick={remove}
              disabled={submitting}
              data-testid="media-link-remove"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] text-red-300 border border-red-500/25 bg-red-500/[0.06] hover:bg-red-500/[0.12] hover:text-red-200 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {submittingMode === 'remove' ? 'Removing…' : 'Remove link'}
            </button>
          ) : <span />}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="media-link-form"
              disabled={submitting}
              data-testid="media-link-submit"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-emerald-500/90 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
              {submittingMode === 'save' ? 'Saving…' : primaryLabel}
            </button>
          </div>
        </div>
      }
    >
      <form id="media-link-form" onSubmit={submit} className="space-y-4">
        {(headerTitle || subtitle) && (
          <div className="text-[13px] text-gray-300">
            {headerTitle && <div className="text-white font-medium">{headerTitle}</div>}
            {subtitle && <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>}
          </div>
        )}

        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">
            Media library URL{mode === 'complete' && (
              <span className="text-gray-500 normal-case tracking-normal"> (optional)</span>
            )}
          </span>
          <input
            type="url"
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/…"
            data-testid="media-link-input"
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600"
            autoFocus
          />
          <span className="block text-[11px] text-gray-500 mt-1">
            {isEdit
              ? "Update the link to point to a different folder, or use Remove link to clear it."
              : "Drive, Dropbox, Google Photos: any link that points to the final assets. Skip if you'll add it later."}
          </span>
        </label>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
